import {
  ArrowBackIosOutlined as ArrowBackIosOutlinedIcon,
  ArrowForwardIosOutlined as ArrowForwardIosOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  MobileStepper,
  Skeleton,
  Step,
  StepButton,
  StepLabel,
  StepLabelProps,
  Stepper,
} from '@mui/material';
import { useGetCurrentCampaignStep } from 'components/Campaign/useGetCurrentCampaignStep';
import { APP_HEADER_ID } from 'components/Header/Header';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { useIsMobileResolution } from 'hooks/useIsMobileResolution';
import { FunctionComponent, ReactNode, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Section } from 'shared/Sections/Section';
import { useUserTracking } from 'userTracking/useUserTracking';
import { getObjectKeys } from 'utils/getObjectKeys';
import { CampaignContext } from '../CampaignContext/CampaignContext';
import { getAggregateStepStatus, StepStatusIconProps } from '../StepStatusIcon';
import { CampaignStep } from '../useGetOrderedCampaignsSteps';
import { getCampaignStepURL } from '../utils/getCampaignStepURL';
import { CampaignStepIcon } from './CampaignStepIcon';
import { LaunchCampaignButton } from './LaunchCampaignButton/LaunchCampaignButton';

export const CAMPAIGN_STEPS_BAR_ID = 'campaign-acs-steps-bar';

const appHeaderHTMLElement = document.getElementById(APP_HEADER_ID);

const CustomStepLabel: FunctionComponent<
  StepLabelProps & { warning?: boolean; touched?: boolean; error?: boolean }
> = ({ warning, touched, error, ...rest }) => {
  return (
    <StepLabel
      {...rest}
      sx={(theme) => {
        // decide color based on touched/error/warning
        const isError = !!error;
        const isWarning = !!warning;
        const isTouched = !!touched;
        // position is important here, we only display warning or error after step is touched
        let labelColor = '';
        if (!isTouched) labelColor = theme.palette.text.secondary;
        else if (isError) labelColor = theme.palette.error.main;
        else if (isWarning) labelColor = theme.palette.warning.main;

        return {
          '& .MuiStepLabel-label': {
            color: labelColor,
            '&.Mui-active': {
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightBold,
            },
          },
        };
      }}
    />
  );
};

const StepsBarWrapper: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  return (
    <Section
      id={CAMPAIGN_STEPS_BAR_ID}
      sx={{
        px: 0,
        py: 0,
        zIndex: 2,
        position: 'sticky',
        top: appHeaderHTMLElement?.offsetHeight ?? 0,
      }}
    >
      <Container
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          height: { xs: 52, md: 77 },
          justifyContent: 'space-between',
        }}
      >
        {children}
      </Container>
    </Section>
  );
};

export const StepsBarSkeleton = () => {
  return (
    <StepsBarWrapper>
      <Skeleton width="100%" height={12} />
    </StepsBarWrapper>
  );
};

export const StepsBar: FunctionComponent = () => {
  const isMobile = useIsMobileResolution();
  const { formatMessage } = useExtendedIntl();
  const { name: currentStep } = useGetCurrentCampaignStep();
  const { captureEvent } = useUserTracking();

  const { isDraft, campaignSteps, campaignPrettyId } = useContext(CampaignContext);

  const orderedSteps = getObjectKeys(campaignSteps);
  const currentStepIndex = orderedSteps.findIndex((step) => step === currentStep);

  function getPreviousRoute() {
    const prevStep = orderedSteps[currentStepIndex - 1];

    return getCampaignStepURL(prevStep, campaignPrettyId);
  }

  function getNextRoute() {
    const nextStep = orderedSteps[currentStepIndex + 1];

    return getCampaignStepURL(nextStep, campaignPrettyId);
  }

  const isLastStep = currentStepIndex === orderedSteps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const handleNavigationClick = (eventName: string, additionalData?: Record<string, unknown>) => {
    captureEvent({
      eventName,
      data: {
        currentStep,
        currentStepIndex,
        campaignPrettyId,
        isDraft,
        ...additionalData,
      },
    });
  };

  // For published campaigns, show a "Go to Dashboard" button
  const dashboardButton = !isDraft ? (
    <Button
      component={RouterLink}
      to={`/campaign/${campaignPrettyId}/dashboard/overview`}
      variant="outlined"
      data-testid="steps-bar.dashboard"
      onClick={() =>
        handleNavigationClick('acs_navigation_dashboard_clicked', {
          source: 'steps_bar',
        })
      }
    >
      {formatMessage({ id: 'shared.go-to-dashboard' })}
    </Button>
  ) : null;

  let nextButton;

  if (!isLastStep) {
    nextButton = (
      <Button
        component={RouterLink}
        to={getNextRoute()}
        variant="text"
        endIcon={<ArrowForwardIosOutlinedIcon />}
        data-testid="steps-bar.next"
        onClick={() =>
          handleNavigationClick('acs_navigation_next_clicked', { nextStep: orderedSteps[currentStepIndex + 1] })
        }
      >
        {formatMessage({ id: 'shared.next' })}
      </Button>
    );
  } else if (isDraft) {
    nextButton = <LaunchCampaignButton />;
  } else {
    // For published campaigns on last step, show dashboard button instead of next
    nextButton = null;
  }

  // Always show "Back to campaigns" button for easy exit
  const backToCampaignsButton = (
    <Button
      component={RouterLink}
      to="/campaigns"
      variant="outlined"
      onClick={() =>
        handleNavigationClick('acs_navigation_back_to_campaigns_clicked', {
          location: isFirstStep ? 'first_step' : 'middle_step',
        })
      }
    >
      {formatMessage({ id: 'shared.back-to-homepage' }, { isMobile: true })}
    </Button>
  );

  const backButton = isFirstStep ? null : (
    <Button
      variant="text"
      to={getPreviousRoute()}
      component={RouterLink}
      disabled={currentStepIndex < 1}
      data-testid="steps-bar.back"
      startIcon={<ArrowBackIosOutlinedIcon />}
      onClick={() =>
        handleNavigationClick('acs_navigation_back_clicked', { previousStep: orderedSteps[currentStepIndex - 1] })
      }
    >
      {formatMessage({ id: 'shared.back' })}
    </Button>
  );

  const renderStep = (step: CampaignStep) => {
    let statusIconProps: StepStatusIconProps | undefined;

    const stepStatus = campaignSteps[step];
    if (stepStatus) {
      const aggregateStatus = getAggregateStepStatus(stepStatus);

      if (aggregateStatus) {
        statusIconProps = { aggregateStatus };
      }
    }

    return (
      <Step key={step} completed={stepStatus?.completed}>
        <StepButton
          disableRipple
          component={RouterLink}
          to={getCampaignStepURL(step, campaignPrettyId)}
          icon={<></>} // keeps layout behavior, without it StepButton adds its own icon
          onClick={() =>
            handleNavigationClick('acs_navigation_step_clicked', {
              targetStep: step,
              targetStepIndex: orderedSteps.findIndex((s) => s === step),
            })
          }
        >
          <CustomStepLabel
            touched={stepStatus?.touched}
            error={stepStatus?.hasBlockingIssues}
            warning={stepStatus?.hasNonBlockingIssues}
            data-testid={`steps-bar.${step}`}
            StepIconComponent={(props) => <CampaignStepIcon {...props} stepStatusIconProps={statusIconProps} />}
          >
            {formatMessage({ id: `campaign-step.${step}` })}
          </CustomStepLabel>
        </StepButton>
      </Step>
    );
  };

  return (
    <StepsBarWrapper>
      {isMobile ? (
        <MobileStepper
          variant="dots"
          position="static"
          nextButton={nextButton || dashboardButton}
          backButton={backButton || backToCampaignsButton}
          steps={orderedSteps.length}
          activeStep={currentStepIndex}
          sx={{
            py: 1,
            px: 0,
            flex: 1,
            backgroundColor: 'background.paper',
          }}
        />
      ) : (
        <>
          <Box
            sx={{
              gap: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {backToCampaignsButton}
            {backButton}
          </Box>
          <Stepper nonLinear sx={{ p: 3, flex: 1 }} activeStep={currentStepIndex}>
            {orderedSteps.map(renderStep)}
          </Stepper>
          <Box
            sx={{
              gap: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {nextButton}
            {dashboardButton}
          </Box>
        </>
      )}
    </StepsBarWrapper>
  );
};
